import { useState, useEffect } from "react";
import { API, axios } from "@/api";
import { Radio, Database, Calendar, TrendingUp, Clock, Loader2 } from "lucide-react";

export default function PredictHQPanel() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/predictiq-stats`)
      .then(r => setStats(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>;
  if (!stats) return <p className="text-xs text-slate-400 text-center py-4">Unable to load PredictHQ stats</p>;

  return (
    <div className="space-y-4" data-testid="predictiq-panel">
      {/* Key Status */}
      <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100">
        <Radio className="w-4 h-4 text-slate-500" />
        <div className="flex-1">
          <p className="text-[10px] text-slate-400 uppercase tracking-wider">API Key Status</p>
          <p className={`text-sm font-bold ${stats.key_active ? "text-emerald-600" : "text-amber-500"}`} data-testid="key-status">
            {stats.key_active ? "Active" : "Placeholder (set PREDICTIQ_API_KEY to activate)"}
          </p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-2">
        <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
          <Database className="w-3.5 h-3.5 text-blue-500 mb-1" />
          <p className="text-[10px] text-slate-400">Cache Hit Rate</p>
          <p className="text-lg font-bold text-slate-800" data-testid="cache-hit-rate">{stats.cache_hit_rate}%</p>
          <p className="text-[9px] text-slate-300">{stats.active_cached} active / {stats.total_cached} total</p>
        </div>
        <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
          <Calendar className="w-3.5 h-3.5 text-emerald-500 mb-1" />
          <p className="text-[10px] text-slate-400">Events Ingested</p>
          <p className="text-lg font-bold text-slate-800" data-testid="events-ingested">{stats.events_ingested_this_week}</p>
          <p className="text-[9px] text-slate-300">this week ({stats.total_predictiq_events} total)</p>
        </div>
      </div>

      {/* Last Sync */}
      <div className="flex items-center gap-2 text-[10px] text-slate-400">
        <Clock className="w-3 h-3" />
        <span>Last sync: {stats.last_sync ? new Date(stats.last_sync).toLocaleString() : "Never"}</span>
      </div>

      {/* Top Upcoming Events */}
      {stats.top_upcoming_events?.length > 0 && (
        <div>
          <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1">
            <TrendingUp className="w-3 h-3" /> Top 5 Upcoming High-Demand Events
          </p>
          <div className="space-y-1.5">
            {stats.top_upcoming_events.map((ev, i) => (
              <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-slate-50 border border-slate-100" data-testid={`top-event-${i}`}>
                <span className="text-[9px] font-bold text-slate-300 w-4">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-bold text-slate-700 truncate">{ev.event_name}</p>
                  <p className="text-[9px] text-slate-400">
                    {ev.next_occurrence ? new Date(ev.next_occurrence).toLocaleDateString() : ""}
                    {ev.predicted_attendance ? ` — ${ev.predicted_attendance.toLocaleString()} est.` : ""}
                  </p>
                </div>
                {ev.demand_score && (
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${
                    ev.demand_score > 0.7 ? "bg-orange-100 text-orange-600" : "bg-slate-100 text-slate-500"
                  }`}>
                    {(ev.demand_score * 100).toFixed(0)}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {!stats.key_active && (
        <p className="text-[10px] text-amber-500 bg-amber-50 rounded-lg p-2.5 border border-amber-100">
          Set PREDICTIQ_API_KEY in .env to activate local event intelligence. Free tier at predicthq.com — 1,000 calls/day.
        </p>
      )}
    </div>
  );
}
